import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import logo from "../../assets/logo1.png";
import { login } from "../../services/authService";
import { handleApiError } from "../../utils/handleApiError";

const schema = Yup.object({
  email: Yup.string()
    .required("Email or Mobile Number is required")
    .test(
      "email-or-phone",
      "Enter a valid email or mobile number",
      function (value) {
        if (!value) return false;

        // Email regex
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        // Phone regex (India-friendly: 10 digits, optional +91)
        const phoneRegex = /^(?:\+91[\-\s]?)?[6-9]\d{9}$/;

        return emailRegex.test(value) || phoneRegex.test(value);
      }
    ),

  password: Yup.string().required("Password is required"),
});

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleForgotPassword = (e) => { e.preventDefault(); navigate("/forgot-password"); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await schema.validate({ email, password }, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const errs = {};
      err.inner.forEach((e) => { errs[e.path] = e.message; });
      setErrors(errs);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await login({ identifier: email, password });
      const user_id =
        res.data?.user_id ||
        res.data?.data?.user_id ||
        res.data?.data?.user?.user_id ||
        "";
      navigate("/verify-code", { state: { email, user_id, source: "login" } });
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputBase =
    "w-full pl-4 pr-4 py-3 rounded-[8px] bg-[#F3F4F5] text-[16px] text-gray-900 outline-none transition-all duration-200 placeholder:text-gray-400 focus:bg-white focus:shadow-[0_0_0_3px_rgba(124,58,237,0.08)]";
  const inputClass = (field) =>
    `${inputBase} ${errors[field] ? "border-red-400 focus:border-red-400" : "border-gray-200 focus:border-violet-500"}`;

  return (
    <div className="flex min-h-screen w-full font-sans">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-[38%] flex-col px-12 py-12 pb-4 relative overflow-hidden bg-gradient-to-br from-[#754F89] to-[#4B2364]">
        <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full bg-violet-400/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-[10%] -left-10 w-64 h-64 rounded-full bg-purple-400/15 blur-3xl pointer-events-none" />
        <div className="relative h-auto max-w-[245px] justify-start">
            <img src={logo} alt="KrestPay" className="w-[200px] h-auto block" />
        </div>
        <div className="relative z-10 mb-24">
          <div className="space-y-5 mb-12">
            <h1 className="text-white font-extrabold text-[48px] leading-[1.0] tracking-tight">
              The Future of Digital Alchemy
            </h1>
            <p className="text-white/55 text-sm leading-relaxed max-w-[300px]">
              Secure, high-speed financial infrastructure for modern merchants. Manage payments, invoices, and DMT with institutional-grade precision.
            </p>
          </div>
        </div>
        <div className="relative z-10 flex items-center mt-18 gap-5 bg-[#593270] border border-[#5A3371] rounded-[16px] px-6 py-4">
          <div className="w-12 h-12 rounded-[12px] bg-white/20 flex items-center justify-center text-white">
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="30" fill="#FFFFFF" viewBox="0 0 24 24" stroke="#a78bfa" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <div>
            <p className="text-white text-[16px] font-semibold">Institutional Grade Security</p>
            <p className="text-purple-100/70 text-[14px] mt-0.5">PCI-DSS compliant infrastructure protecting every transaction.</p>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 lg:w-[60%] flex items-center justify-center bg-[#FFFFFF] pt-20px px-4 py-12">
        <div className="w-full max-w-[420px]">
          <div className="flex lg:hidden items-center gap-2 mb-8 justify-center">
            <img src={logo} alt="KrestPay" className="w-24 h-24 rounded-xl object-cover" />
            {/* <span className="font-bold text-lg text-[#0f0528]">KrestPay</span> */}
          </div>
          <div className="mt-6 p-6 pb-1">
            <h2 className="text-[#191C1D] font-extrabold tracking-tight text-[30px] mb-4">Welcome Back</h2>
            <p className="text-[#4C444E] text-md">Access your partner dashboard to manage your <br/> merchants.</p>
          </div>
          <div className="bg-white p-6 pt-2">
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold tracking-wide text-[#4C444E] mb-1.5 uppercase">Email or Mobile Number</label>
                <input
                  type="email" inputMode="email" autoComplete="email" placeholder="john@company.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: undefined })); }}
                  className={inputClass("email")}
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-[12px] font-semibold tracking-wide text-[#4C444E] mb-1.5 uppercase">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"} autoComplete="current-password" placeholder="••••••••"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: undefined })); }}
                    className={`${inputClass("password")} pr-11`}
                  />
                  <button type="button" onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                      {showPassword
                        ? <><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /><circle cx="12" cy="12" r="3" /></>
                        : <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.97 9.97 0 012.563-4.568M6.228 6.228A9.97 9.97 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.97 9.97 0 01-4.423 5.568M6.228 6.228L3 3m3.228 3.228l3.65 3.65M17.772 17.772L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      }
                    </svg>
                  </button>
                </div>
                {password.length > 0 && (
                  <div className="flex gap-1 mt-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                        password.length >= i * 3
                          ? password.length < 6 ? "bg-red-400" : password.length < 10 ? "bg-amber-400" : "bg-emerald-500"
                          : "bg-gray-200"
                      }`} />
                    ))}
                  </div>
                )}
                {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
              </div>

              <label className="flex items-start gap-3 cursor-pointer group">
                <div onClick={() => setAgreed((v) => !v)}
                  className={`mt-0.5 w-4 h-4 rounded shrink-0 border-1 border-[#CEC3D0] bg-[#E1E3E4] flex items-center justify-center transition-all ${agreed ? "bg-violet-600 border-violet-600" : "border-gray-300 group-hover:border-violet-400"}`}>
                  {agreed && <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>}
                </div>
                <span className="text-[14px] text-[#4C444E] font-medium leading-relaxed">Remember me</span>
                <div className="ml-auto">
                  <a href="#" onClick={handleForgotPassword} className="text-[14px] text-[#34094D] font-semibold hover:underline">Forgot password?</a>
                </div>
              </label>

              <button type="submit" disabled={isSubmitting}
                className={`w-full py-3.5 rounded-xl text-[#FFFFFF] text-[18px] font-bold tracking-wide transition-all duration-200 bg-[linear-gradient(97.63deg,_#EA8534_0%,_#A0111A_100%)] shadow-[0px_12px_24px_0px_#EA85344D] flex items-center justify-center gap-2 ${isSubmitting ? "opacity-70 cursor-not-allowed" : "hover:opacity-90 active:scale-[0.99] cursor-pointer"}`}>
                {isSubmitting && <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeOpacity="0.3" /><path d="M12 2a10 10 0 0 1 10 10" /></svg>}
                {isSubmitting ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </div>
          <p className="text-center mt- text-[14px] text-[#4C444E]">
            Don't have a partner account?{" "}
            <Link to="/register" className="text-[#34094D] text-[14px] font-semibold hover:underline">Sign Up Now</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
